import React from 'react';
import PropTypes from 'prop-types';
import postal from 'postal';
import toastr from 'toastr';
import Validator from '../../shared/validator';
import Loading from '../../shared/loading';
import parseFailXhr from '../../shared/parse-fail-xhr';
import { setPersonalDetails } from '../../services/onboarding-services';
import { MINIMUM_PASSWORD_LENGTH } from '../../shared/consts';

export default class OnboardingProvidePersonalDetailsModal extends React.Component {
  static propTypes = {
    active: PropTypes.bool.isRequired,
    onComplete: PropTypes.func.isRequired,
    currentStepIndex: PropTypes.number.isRequired,
    totalStepCount: PropTypes.number.isRequired,
    provideName: PropTypes.bool.isRequired,
    providePassword: PropTypes.bool.isRequired,
  }

  constructor(props, context) {
    super(props, context);
    this.handleSubmit = this.handleSubmit.bind(this);
    this.handlePasswordChange = this.handlePasswordChange.bind(this);
    this.handleVerifyPasswordChange = this.handleVerifyPasswordChange.bind(this);
    this.handleFirstNameChange = this.handleFirstNameChange.bind(this);
    this.handleLastNameChange = this.handleLastNameChange.bind(this);

    this.state = {
      password: '',
      verifyPassword: '',
      firstName: '',
      lastName: '',
      userAttemptedToDismiss: false,
      validator: new Validator({
        passwordRequired: s => !this.props.providePassword || !!s.password,
        passwordCorrectLength: s => !this.props.providePassword || !s.password
          || s.password.length >= MINIMUM_PASSWORD_LENGTH,
        passwordContainsDigit: s => !this.props.providePassword || !s.password
          || /[0-9]/.test(s.password),
        verifyPasswordRequired: s => !this.props.providePassword || !!s.verifyPassword,
        passwordsMustMatch: s => !this.props.providePassword || !s.password || !s.verifyPassword
          || s.password === s.verifyPassword,
        userDidNotAttemptToDismiss: s => !this.props.providePassword || !s.userAttemptedToDismiss,
        firstNameRequired: s => !this.props.provideName || !!s.firstName,
      }),
    };
  }

  componentDidMount() {
    if (this.props.active) {
      this.$modal.show();
    }

    const self = this;
    this.$modal.click(function onClick(e) {
      if (e.target === this) {
        self.setState(state => {
          state.userAttemptedToDismiss = true;
          state.validator.validateForm(state);
        });
      }
    });
  }

  componentDidUpdate(prevProps) {
    if (!prevProps.active && this.props.active) {
      this.$modal.fadeIn(800);
    }
  }

  handleSubmit(event) {
    event.preventDefault();

    this.setState(
      state => state.validator.validateForm(state),
      () => {
        if (!this.state.validator.formValid) return;

        const { password, firstName, lastName } = this.state;
        this.loading.start();
        setPersonalDetails(password, firstName, lastName).then(() => {
          this.$modal.fadeOut(800, () => this.loading.stop());
          this.props.onComplete();
          postal.publish({
            channel: 'events.user',
            topic: 'first-name-changed',
            data: { firstName },
          });
        }).catch(err => {
          this.loading.stop();
          const errors = parseFailXhr(err.jqXHR);
          errors.anyUnhandledErrors() && toastr.error(errors.getErrorSummary(), 'Error');
        });
      },
    );
  }

  handlePasswordChange(event) {
    const password = event.target.value;

    this.setState(state => {
      state.password = password;
      state.userAttemptedToDismiss = false;
      state.validator.validatePasswordRequired(state);

      return state;
    });
  }

  handleVerifyPasswordChange(event) {
    const verifyPassword = event.target.value;

    this.setState(state => {
      state.verifyPassword = verifyPassword;
      state.userAttemptedToDismiss = false;
      state.validator.validateVerifyPasswordRequired(state);
      state.validator.validatePasswordsMustMatch(state);

      return state;
    });
  }

  handleFirstNameChange(event) {
    const firstName = event.target.value;

    this.setState(state => {
      state.firstName = firstName;
      state.validator.validateFirstNameRequired(state);

      return state;
    });
  }

  handleLastNameChange(event) {
    const lastName = event.target.value;

    this.setState({ lastName });
  }

  renderHeader() {
    const {
      provideName,
      providePassword,
      totalStepCount: total,
      currentStepIndex: current,
    } = this.props;
    return (
      <header>
        {total <= 1 || current === -1 || (
          <p className="step">{`Step ${current + 1} of ${total}`}</p>
        )}
        <h4>
          {provideName && providePassword && <span>Provide some personal details</span>}
          {provideName && !providePassword && <span>Provide your name</span>}
          {providePassword && !provideName && <span>Create a password</span>}
        </h4>
      </header>
    );
  }

  renderFirstName() {
    if (!this.props.provideName) {
      return null;
    }

    return (
      <p className={`form-group ${this.state.validator.firstNameRequiredValid || 'has-error'}`}>
        <input
          type="text"
          placeholder="First name"
          id="firstName"
          onChange={this.handleFirstNameChange}
        />
        {this.state.validator.firstNameRequiredValid || (
          <span className="help-block">First name is required</span>
        )}
      </p>
    );
  }

  renderLastName() {
    if (!this.props.provideName) {
      return null;
    }

    return (
      <p className="form-group">
        <input
          type="text"
          placeholder="Last name"
          id="lastName"
          onChange={this.handleLastNameChange}
        />
      </p>
    );
  }

  renderPassword() {
    if (!this.props.providePassword) {
      return null;
    }

    const v = this.state.validator;
    const passwordValid = !v.userDidNotAttemptToDismissValid
      || v.passwordRequiredValid && v.passwordCorrectLengthValid;
    const hideRequiredMessage = !v.userDidNotAttemptToDismissValid || v.passwordRequiredValid;
    const hideLengthMessage = !v.userDidNotAttemptToDismissValid || v.passwordCorrectLengthValid;
    const hideDigitMessage = !v.userDidNotAttemptToDismissValid || v.passwordContainsDigitValid;
    return (
      <p className={`form-group ${passwordValid || 'has-error'}`}>
        <input
          type="password"
          placeholder="Password"
          onChange={this.handlePasswordChange}
        />
        {hideRequiredMessage || <span className="help-block">Password is required</span>}
        {hideLengthMessage || (
          <span className="help-block">
            {`Password must be at least ${MINIMUM_PASSWORD_LENGTH} characters`}
          </span>
        )}
        {hideDigitMessage || (
          <span className="help-block">Password must include at least one digit</span>
        )}
      </p>
    );
  }

  renderRepeatPassword() {
    if (!this.props.providePassword) {
      return null;
    }

    const v = this.state.validator;
    const verifyPasswordValid = !v.userDidNotAttemptToDismissValid
      || v.verifyPasswordRequiredValid && v.passwordsMustMatchValid;
    const hideRequiredMessage = !v.userDidNotAttemptToDismissValid || v.verifyPasswordRequiredValid;
    const hideMatchMessage = !v.userDidNotAttemptToDismissValid || v.passwordsMustMatchValid;
    return (
      <p className={`form-group ${verifyPasswordValid || 'has-error'}`}>
        <input
          type="password"
          placeholder="Verify password"
          onChange={this.handleVerifyPasswordChange}
        />
        {hideRequiredMessage || <span className="help-block">Verify password is required</span>}
        {hideMatchMessage || <span className="help-block">Passwords must match</span>}
      </p>
    );
  }

  renderErrorMessage() {
    if (this.state.validator.userDidNotAttemptToDismissValid) {
      return null;
    }

    const { provideName, providePassword } = this.props;
    const showMessage = providePassword || provideName;
    const msg = providePassword
      ? `You must proviee ${provideName ? 'your name and ' : ''}a password for your account`
      : 'You must provide your name';
    return (
      <p className="has-error">
        <span className="help-block">
          {showMessage && <span>{msg}</span>}
        </span>
      </p>
    );
  }

  render() {
    const { provideName, providePassword } = this.props;

    return (
      <div
        ref={e => (this.$modal = $(e))}
        className="livewire-modal onboarding-provide-personal-details-modal"
        tabIndex="-1"
        role="dialog"
      >
        <article>
          {this.renderHeader()}
          <section>
            {provideName && providePassword && (
              <p className="step-instructions">
                {'Enter your name and a password for your new account. '}
                {
                  `Your password must be at least ${MINIMUM_PASSWORD_LENGTH} characters long,`
                    + ' and must include at least one digit.'
                }

              </p>
            )}
            {!provideName && providePassword && (
              <p className="step-instructions">
                {'Enter a password for your new account. '}
                {
                  `It must be at least ${MINIMUM_PASSWORD_LENGTH} characters long,`
                    + ' and must include at least one digit.'
                }
              </p>
            )}

            <form onSubmit={this.handleSubmit}>
              <Loading ref={e => (this.loading = e)} variant="small">
                <React.Fragment>
                  <div className="livewire-modal-form-container">
                    {this.renderFirstName()}
                    {this.renderLastName()}
                    {this.renderPassword()}
                    {this.renderRepeatPassword()}
                  </div>
                  {this.renderErrorMessage()}
                  <p className="form-group submit-group">
                    <button type="submit" className="btn btn-primary">Continue</button>
                  </p>
                </React.Fragment>
              </Loading>
            </form>
          </section>
        </article>
      </div>
    );
  }
}
